/* RESPONSIBLE TEAM: team-phone */
/* eslint-disable @intercom/intercom/no-bare-strings */

import type { FinVoiceVoiceLanguage } from './fin-voice-language-options';

export type FinVoiceVoiceOptions = {
  value: FinVoiceVoice;
  text: string;
};

// Universal voices (support 4+ languages) with neutral accent
export type FinVoiceUniversalVoice =
  | 'Mark'
  | 'Sully'
  | 'Archer'
  | 'Paul'
  | 'Felicity'
  | 'Nichalia'
  | 'Hope'
  | 'Chris'
  | 'Juniper';

// English voices
export type FinVoiceEnglishVoice =
  | 'Alexander'
  | 'Hannah'
  | 'Jerry'
  | 'Oxley'
  | 'Chelsea'
  | 'Cassidy'
  | 'Lydia'
  | 'Jack'
  | 'April'
  | 'Rory'
  | 'Labhaoise';

// Spanish voices
export type FinVoiceSpanishVoice = 'Carolina' | 'Ruiz' | 'Dora' | 'Lizy' | 'Paloma';

// Danish voices
export type FinVoiceDanishVoice = 'Matias' | 'Søren';

// Dutch voices
export type FinVoiceDutchVoice = 'Ruth' | 'Serge' | 'Arjen';

// French voices
export type FinVoiceFrenchVoice = 'Nicolas' | 'Lucie' | 'Alexandre';

// German voices
export type FinVoiceGermanVoice = 'Felix' | 'Ramona' | 'Lea' | 'Susi';

// Polish voices
export type FinVoicePolishVoice = 'Pawel' | 'Konwersacyjny' | 'Beata';

// Italian voices
export type FinVoiceItalianVoice = 'Violetta' | 'Raffaele';

// Arabic voices
export type FinVoiceArabicVoice = 'Salma' | 'Haytham' | 'Saud';

// Japanese voices
export type FinVoiceJapaneseVoice = 'Kaori';

// Korean voices
export type FinVoiceKoreanVoice = 'Han Aim' | 'Harry Kim';

// Norwegian voices
export type FinVoiceNorwegianVoice = 'Helge' | 'Johannes';

// Russian voices
export type FinVoiceRussianVoice = 'Olga' | 'Arcadays';

// Swedish voices
export type FinVoiceSwedishVoice = 'Ola' | 'Sanna' | 'Tommy' | 'Eva' | 'Jonas' | 'Olivia';

// Chinese voices
export type FinVoiceChineseVoice = 'Amy' | 'Yu' | 'Tong Wong';

// Hindi voices
export type FinVoiceHindiVoice = 'Anjali' | 'Raju';

// Indonesian voices
export type FinVoiceIndonesianVoice = 'Putra' | 'Zephlyn';

// Turkish voices
export type FinVoiceTurkishVoice = 'Orbay' | 'Meta Ecem';

// Ukrainian voices
export type FinVoiceUkrainianVoice = 'Mariya' | 'Alex';

// Vietnamese voices
export type FinVoiceVietnameseVoice = 'Nguyễn Luci' | 'Huyen';

// Czech voices
export type FinVoiceCzechVoice = 'Adam' | 'Oliver';

// Bulgarian voices
export type FinVoiceBulgarianVoice = 'Milena';

// Greek voices
export type FinVoiceGreekVoice = 'Theos' | 'Christos';

// Romanian voices
export type FinVoiceRomanianVoice = 'Mike' | 'Anca';

// Malay voices
export type FinVoiceMalayVoice = 'Athira' | 'Aisyah';

// Hungarian voices
export type FinVoiceHungarianVoice = 'Nolen' | 'David';

// Finnish voices
export type FinVoiceFinnishVoice = 'Jaakko';

// Portuguese voices
export type FinVoicePortugueseVoice = 'Dinis' | 'Roberta' | 'Kaique';

// Union type of all voices
export type FinVoiceVoice =
  | FinVoiceUniversalVoice
  | FinVoiceEnglishVoice
  | FinVoiceSpanishVoice
  | FinVoiceDanishVoice
  | FinVoiceDutchVoice
  | FinVoiceFrenchVoice
  | FinVoiceGermanVoice
  | FinVoicePolishVoice
  | FinVoiceItalianVoice
  | FinVoiceArabicVoice
  | FinVoiceJapaneseVoice
  | FinVoiceKoreanVoice
  | FinVoiceNorwegianVoice
  | FinVoiceRussianVoice
  | FinVoiceSwedishVoice
  | FinVoiceChineseVoice
  | FinVoiceHindiVoice
  | FinVoiceIndonesianVoice
  | FinVoiceTurkishVoice
  | FinVoiceUkrainianVoice
  | FinVoiceVietnameseVoice
  | FinVoiceCzechVoice
  | FinVoiceBulgarianVoice
  | FinVoiceGreekVoice
  | FinVoiceRomanianVoice
  | FinVoiceMalayVoice
  | FinVoiceHungarianVoice
  | FinVoiceFinnishVoice
  | FinVoicePortugueseVoice;

export const FIN_VOICE_11LABS_VOICES: {
  value: FinVoiceVoice;
  text: string;
  description: string[];
  supportedLanguages: string[];
  dateAdded: string;
}[] = [
  // Universal voices
  {
    value: 'Mark',
    text: 'Mark',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: [
      'en',
      'ar',
      'da',
      'de',
      'es',
      'fr',
      'it',
      'ja',
      'ko',
      'nl',
      'no',
      'pl',
      'ru',
      'sv',
      'ta',
      'hi',
      'cs',
      'ro',
      'hu',
      'tr',
      'uk',
      'pt',
    ],
    dateAdded: '2025-03-10',
  },
  {
    value: 'Sully',
    text: 'Sully',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: [
      'en',
      'ar',
      'de',
      'es',
      'fr',
      'it',
      'ja',
      'nl',
      'ru',
      'sv',
      'hi',
      'pt',
      'el',
      'cs',
      'bg',
      'tr',
      'uk',
    ],
    dateAdded: '2025-07-07',
  },
  {
    value: 'Jerry',
    text: 'Jerry',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en'],
    dateAdded: '2025-07-07',
  },
  {
    value: 'Oxley',
    text: 'Oxley',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en'],
    dateAdded: '2025-07-07',
  },
  {
    value: 'Nichalia',
    text: 'Nichalia',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: [
      'en',
      'de',
      'es',
      'fr',
      'it',
      'ko',
      'ru',
      'sv',
      'cs',
      'pl',
      'hi',
      'tr',
      'hu',
      'ro',
      'zh',
      'fi',
      'pt',
      'el',
      'ta',
    ],
    dateAdded: '2025-03-06',
  },
  {
    value: 'Hope',
    text: 'Hope',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: [
      'en',
      'es',
      'fr',
      'it',
      'ru',
      'ta',
      'pt',
      'cs',
      'bg',
      'tr',
      'hi',
      'hu',
      'ro',
      'el',
    ],
    dateAdded: '2025-03-06',
  },
  {
    value: 'Chelsea',
    text: 'Chelsea',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en'],
    dateAdded: '2025-07-07',
  },
  {
    value: 'Lydia',
    text: 'Lydia',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en'],
    dateAdded: '2025-10-24',
  },
  {
    value: 'Jack',
    text: 'Jack',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en'],
    dateAdded: '2025-10-24',
  },
  {
    value: 'April',
    text: 'April',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en'],
    dateAdded: '2025-10-24',
  },
  {
    value: 'Cassidy',
    text: 'Cassidy',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en'],
    dateAdded: '2025-03-06',
  },
  {
    value: 'Juniper',
    text: 'Juniper',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en', 'ar', 'da', 'de', 'fr', 'it', 'pl', 'pt', 'hi', 'cs', 'tr', 'ro'],
    dateAdded: '2025-03-06',
  },
  // British voices
  {
    value: 'Chris',
    text: 'Chris',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.british-voice',
    ],
    supportedLanguages: [
      'en',
      'es',
      'fr',
      'ja',
      'ko',
      'nl',
      'no',
      'pl',
      'ru',
      'sv',
      'bg',
      'el',
      'tr',
      'hu',
      'fi',
      'cs',
      'ta',
    ],
    dateAdded: '2025-03-06',
  },
  {
    value: 'Archer',
    text: 'Archer',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.british-voice',
    ],
    supportedLanguages: [
      'en',
      'da',
      'es',
      'fr',
      'it',
      'nl',
      'ru',
      'sv',
      'pt',
      'hi',
      'pl',
      'el',
      'tr',
      'hu',
      'ro',
      'ta',
    ],
    dateAdded: '2025-03-11',
  },
  {
    value: 'Alexander',
    text: 'Alexander',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.british-voice',
    ],
    supportedLanguages: ['en'],
    dateAdded: '2025-04-11',
  },
  {
    value: 'Felicity',
    text: 'Felicity',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.british-voice',
    ],
    supportedLanguages: [
      'en',
      'da',
      'es',
      'fr',
      'it',
      'no',
      'ru',
      'sv',
      'hi',
      'bg',
      'pt',
      'fi',
      'cs',
    ],
    dateAdded: '2025-06-13',
  },
  // Australian voices
  {
    value: 'Paul',
    text: 'Paul',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.australian-voice',
    ],
    supportedLanguages: [
      'en',
      'da',
      'es',
      'it',
      'nl',
      'pl',
      'ru',
      'sv',
      'pt',
      'hi',
      'tr',
      'hu',
      'cs',
      'el',
    ],
    dateAdded: '2025-04-11',
  },
  {
    value: 'Hannah',
    text: 'Hannah',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.australian-voice',
    ],
    supportedLanguages: ['en'],
    dateAdded: '2025-04-11',
  },
  // Irish voices
  {
    value: 'Rory',
    text: 'Rory',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.irish-voice',
    ],
    supportedLanguages: ['en'],
    dateAdded: '2025-06-13',
  },
  {
    value: 'Labhaoise',
    text: 'Labhaoise',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.irish-voice',
    ],
    supportedLanguages: ['en'],
    dateAdded: '2025-06-13',
  },
  // Spanish voices
  {
    value: 'Carolina',
    text: 'Carolina',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.peninsular-spanish-voice',
    ],
    supportedLanguages: ['es'],
    dateAdded: '2025-08-01',
  },
  {
    value: 'Ruiz',
    text: 'Ruiz',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.peninsular-spanish-voice',
    ],
    supportedLanguages: ['es'],
    dateAdded: '2025-08-01',
  },
  {
    value: 'Dora',
    text: 'Dora Lanarra',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.latin-american-spanish-voice',
    ],
    supportedLanguages: ['es'],
    dateAdded: '2025-09-02',
  },
  {
    value: 'Lizy',
    text: 'Lizy',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.latin-american-spanish-voice',
    ],
    supportedLanguages: ['es'],
    dateAdded: '2025-09-02',
  },
  {
    value: 'Paloma',
    text: 'Paloma',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.latin-american-spanish-voice',
    ],
    supportedLanguages: ['es'],
    dateAdded: '2025-09-02',
  },
  // Italian voices
  {
    value: 'Violetta',
    text: 'Violetta',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.italian-voice',
    ],
    supportedLanguages: ['es', 'it'],
    dateAdded: '2025-09-24',
  },
  {
    value: 'Raffaele',
    text: 'Raffaele',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.italian-voice',
    ],
    supportedLanguages: ['it'],
    dateAdded: '2025-09-24',
  },
  // Polish voices
  {
    value: 'Pawel',
    text: 'Pawel',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.polish-voice',
    ],
    supportedLanguages: ['pl'],
    dateAdded: '2025-09-24',
  },
  {
    value: 'Konwersacyjny',
    text: 'Konwersacyjny',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.polish-voice',
    ],
    supportedLanguages: ['pl'],
    dateAdded: '2025-09-24',
  },
  {
    value: 'Beata',
    text: 'Beata',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.polish-voice',
    ],
    supportedLanguages: ['pl'],
    dateAdded: '2025-09-24',
  },
  // German voices
  {
    value: 'Felix',
    text: 'Felix',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.german-voice',
    ],
    supportedLanguages: ['de'],
    dateAdded: '2025-09-24',
  },
  {
    value: 'Susi',
    text: 'Susi',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.german-voice',
    ],
    supportedLanguages: ['de'],
    dateAdded: '2025-10-29',
  },
  {
    value: 'Ramona',
    text: 'Ramona',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.german-voice',
    ],
    supportedLanguages: ['de'],
    dateAdded: '2025-09-24',
  },
  {
    value: 'Lea',
    text: 'Lea',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.german-voice',
    ],
    supportedLanguages: ['de'],
    dateAdded: '2025-09-30',
  },
  // French voices
  {
    value: 'Nicolas',
    text: 'Nicolas',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.french-voice',
    ],
    supportedLanguages: ['fr'],
    dateAdded: '2025-09-24',
  },
  {
    value: 'Lucie',
    text: 'Lucie',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.french-voice',
    ],
    supportedLanguages: ['fr'],
    dateAdded: '2025-09-24',
  },
  {
    value: 'Alexandre',
    text: 'Alexandre',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.quebec-french-voice',
    ],
    supportedLanguages: ['fr'],
    dateAdded: '2025-09-24',
  },
  // Dutch voices
  {
    value: 'Ruth',
    text: 'Ruth',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.dutch-voice',
    ],
    supportedLanguages: ['nl'],
    dateAdded: '2025-09-24',
  },
  {
    value: 'Serge',
    text: 'Serge',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.dutch-voice',
    ],
    supportedLanguages: ['nl'],
    dateAdded: '2025-09-24',
  },
  {
    value: 'Arjen',
    text: 'Arjen',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.dutch-voice',
    ],
    supportedLanguages: ['nl'],
    dateAdded: '2025-09-24',
  },
  // Danish voices
  {
    value: 'Søren',
    text: 'Søren',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.danish-voice',
    ],
    supportedLanguages: ['da'],
    dateAdded: '2025-09-24',
  },
  {
    value: 'Matias',
    text: 'Matias',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.jutlandic-danish-voice',
    ],
    supportedLanguages: ['da'],
    dateAdded: '2025-09-24',
  },
  // Arabic voices
  {
    value: 'Salma',
    text: 'Salma',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.levantine-arabic-voice',
    ],
    supportedLanguages: ['ar', 'en'],
    dateAdded: '2025-09-26',
  },
  {
    value: 'Haytham',
    text: 'Haytham',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.egyptian-arabic-voice',
    ],
    supportedLanguages: ['ar'],
    dateAdded: '2025-09-26',
  },
  {
    value: 'Saud',
    text: 'Saud',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.saudi-arabic-voice',
    ],
    supportedLanguages: ['ar'],
    dateAdded: '2026-01-07',
  },
  // Korean voices
  {
    value: 'Han Aim',
    text: 'Han Aim',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.korean-voice',
    ],
    supportedLanguages: ['ko'],
    dateAdded: '2025-09-26',
  },
  {
    value: 'Harry Kim',
    text: 'Harry Kim',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.korean-voice',
    ],
    supportedLanguages: ['ko'],
    dateAdded: '2025-09-26',
  },
  // Japanese voices
  {
    value: 'Kaori',
    text: 'Kaori',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.japanese-voice',
    ],
    supportedLanguages: ['ja'],
    dateAdded: '2025-09-26',
  },
  // Norwegian voices
  {
    value: 'Helge',
    text: 'Helge',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.oslo-norwegian-voice',
    ],
    supportedLanguages: ['no'],
    dateAdded: '2025-09-26',
  },
  {
    value: 'Johannes',
    text: 'Johannes',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.norwegian-voice',
    ],
    supportedLanguages: ['no'],
    dateAdded: '2025-09-26',
  },
  // Swedish voices
  {
    value: 'Ola',
    text: 'Ola',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.swedish-voice',
    ],
    supportedLanguages: ['sv'],
    dateAdded: '2025-09-26',
  },
  {
    value: 'Sanna',
    text: 'Sanna',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.stockholm-swedish-voice',
    ],
    supportedLanguages: ['sv'],
    dateAdded: '2025-09-26',
  },
  {
    value: 'Tommy',
    text: 'Tommy',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.swedish-voice',
    ],
    supportedLanguages: ['sv'],
    dateAdded: '2025-12-08',
  },
  {
    value: 'Eva',
    text: 'Eva',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.swedish-voice',
    ],
    supportedLanguages: ['sv'],
    dateAdded: '2025-12-08',
  },
  {
    value: 'Jonas',
    text: 'Jonas',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.swedish-voice',
    ],
    supportedLanguages: ['sv'],
    dateAdded: '2025-12-08',
  },
  {
    value: 'Olivia',
    text: 'Olivia',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.swedish-voice',
    ],
    supportedLanguages: ['sv'],
    dateAdded: '2025-12-08',
  },
  // Russian voices
  {
    value: 'Arcadays',
    text: 'Arcadays',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.russian-voice',
    ],
    supportedLanguages: ['ru'],
    dateAdded: '2025-09-26',
  },
  {
    value: 'Olga',
    text: 'Olga',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.russian-voice',
    ],
    supportedLanguages: ['ru'],
    dateAdded: '2025-09-26',
  },
  // Chinese voices
  {
    value: 'Amy',
    text: 'Amy',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.beijing-mandarin-voice',
    ],
    supportedLanguages: ['zh'],
    dateAdded: '2025-09-29',
  },
  {
    value: 'Yu',
    text: 'Yu',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.taiwanese-mandarin-voice',
    ],
    supportedLanguages: ['zh'],
    dateAdded: '2025-09-29',
  },
  {
    value: 'Tong Wong',
    text: 'Tong Wong',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.hong-kong-cantonese-voice',
    ],
    supportedLanguages: ['zh'],
    dateAdded: '2025-09-29',
  },
  // Hindi voices
  {
    value: 'Anjali',
    text: 'Anjali',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.hindi-voice',
    ],
    supportedLanguages: ['de', 'hi'],
    dateAdded: '2025-09-29',
  },
  {
    value: 'Raju',
    text: 'Raju',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.hindi-voice',
    ],
    supportedLanguages: ['hi'],
    dateAdded: '2025-09-29',
  },
  // Indonesian voices
  {
    value: 'Putra',
    text: 'Putra',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.indonesian-voice',
    ],
    supportedLanguages: ['id'],
    dateAdded: '2025-09-29',
  },
  {
    value: 'Zephlyn',
    text: 'Zephlyn',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.balinese-indonesian-voice',
    ],
    supportedLanguages: ['id'],
    dateAdded: '2025-09-29',
  },
  // Turkish voices
  {
    value: 'Orbay',
    text: 'Orbay',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.turkish-voice',
    ],
    supportedLanguages: ['tr'],
    dateAdded: '2025-09-29',
  },
  {
    value: 'Meta Ecem',
    text: 'Meta Ecem',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.istanbul-turkish-voice',
    ],
    supportedLanguages: ['tr'],
    dateAdded: '2025-09-29',
  },
  // Ukrainian voices
  {
    value: 'Mariya',
    text: 'Mariya',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.ukrainian-voice',
    ],
    supportedLanguages: ['uk'],
    dateAdded: '2025-10-01',
  },
  {
    value: 'Alex',
    text: 'Alex',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.ukrainian-voice',
    ],
    supportedLanguages: ['uk'],
    dateAdded: '2025-09-29',
  },
  // Vietnamese voices
  {
    value: 'Nguyễn Luci',
    text: 'Nguyễn Luci',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.vietnamese-voice',
    ],
    supportedLanguages: ['vi'],
    dateAdded: '2025-09-29',
  },
  {
    value: 'Huyen',
    text: 'Huyen',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.central-vietnamese-voice',
    ],
    supportedLanguages: ['vi'],
    dateAdded: '2025-09-29',
  },
  // Czech voices
  {
    value: 'Adam',
    text: 'Adam',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.czech-voice',
    ],
    supportedLanguages: ['cs', 'sv'],
    dateAdded: '2025-09-30',
  },
  {
    value: 'Oliver',
    text: 'Oliver',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.czech-voice',
    ],
    supportedLanguages: ['cs'],
    dateAdded: '2025-09-30',
  },
  // Bulgarian voices
  {
    value: 'Milena',
    text: 'Milena',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.bulgarian-voice',
    ],
    supportedLanguages: ['bg'],
    dateAdded: '2025-09-30',
  },
  // Greek voices
  {
    value: 'Theos',
    text: 'Theos',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.greek-voice',
    ],
    supportedLanguages: ['el'],
    dateAdded: '2025-09-30',
  },
  {
    value: 'Christos',
    text: 'Christos',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.greek-voice',
    ],
    supportedLanguages: ['el'],
    dateAdded: '2025-09-30',
  },
  // Romanian voices
  {
    value: 'Mike',
    text: 'Mike',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.romanian-voice',
    ],
    supportedLanguages: ['ro'],
    dateAdded: '2025-09-30',
  },
  {
    value: 'Anca',
    text: 'Anca',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.romanian-voice',
    ],
    supportedLanguages: ['ro'],
    dateAdded: '2025-09-30',
  },
  // Malay voices
  {
    value: 'Athira',
    text: 'Athira',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.malay-voice',
    ],
    supportedLanguages: ['ms'],
    dateAdded: '2025-09-30',
  },
  {
    value: 'Aisyah',
    text: 'Aisyah',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.malay-voice',
    ],
    supportedLanguages: ['en', 'ms'],
    dateAdded: '2025-09-30',
  },
  // Hungarian voices
  {
    value: 'Nolen',
    text: 'Nolen',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.hungarian-voice',
    ],
    supportedLanguages: ['hu'],
    dateAdded: '2025-09-30',
  },
  {
    value: 'David',
    text: 'David',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.hungarian-voice',
    ],
    supportedLanguages: ['hu'],
    dateAdded: '2025-09-30',
  },
  // Finnish voices
  {
    value: 'Jaakko',
    text: 'Jaakko',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.finnish-voice',
    ],
    supportedLanguages: ['fi'],
    dateAdded: '2025-09-30',
  },
  // Portuguese voices
  {
    value: 'Dinis',
    text: 'Dinis',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.portuguese-voice',
    ],
    supportedLanguages: ['pt'],
    dateAdded: '2025-10-02',
  },
  {
    value: 'Roberta',
    text: 'Roberta',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.brazilian-portuguese-voice',
    ],
    supportedLanguages: ['pt'],
    dateAdded: '2025-10-02',
  },
  {
    value: 'Kaique',
    text: 'Kaique',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.brazilian-portuguese-voice',
    ],
    supportedLanguages: ['pt'],
    dateAdded: '2025-10-02',
  },
];

export const DEFAULT_VOICE = 'Hope';

/**
 * Helper function to get ElevenLabs voices that support a specific language.
 * @param language - The language code (e.g., 'en-US', 'es-ES') or 'multilingual'
 * @returns Array of ElevenLabs voice objects that support the language
 */
export function getVoicesForLanguage(
  language: FinVoiceVoiceLanguage,
): typeof FIN_VOICE_11LABS_VOICES {
  // For multilingual mode, return voices that support multiple languages
  if (language === 'multilingual') {
    return FIN_VOICE_11LABS_VOICES.filter((voice) => voice.supportedLanguages.length > 1).sort(
      (a, b) => a.text.localeCompare(b.text),
    );
  }

  // Extract the base language code (e.g., 'en' from 'en-US')
  let baseLanguage = language.split('-')[0];

  // Filter ElevenLabs voices using their supportedLanguages property
  return FIN_VOICE_11LABS_VOICES.filter((voice) =>
    voice.supportedLanguages.includes(baseLanguage),
  ).sort((a, b) => a.text.localeCompare(b.text));
}

/**
 * Helper function to check if an ElevenLabs voice supports a specific language.
 * @param voice - The voice name
 * @param language - The language code (e.g., 'en-US', 'es-ES') or 'multilingual'
 * @returns Boolean indicating if the voice supports the language
 */
export function doesVoiceSupportLanguage(
  voice: FinVoiceVoice,
  language: FinVoiceVoiceLanguage,
): boolean {
  // Find the ElevenLabs voice
  let elevenLabsVoice = FIN_VOICE_11LABS_VOICES.find((v) => v.value === voice);

  if (!elevenLabsVoice) {
    return false;
  }

  // For multilingual mode, check if voice supports multiple languages
  if (language === 'multilingual') {
    return elevenLabsVoice.supportedLanguages.length > 1;
  }

  let baseLanguage = language.split('-')[0];
  return elevenLabsVoice.supportedLanguages.includes(baseLanguage);
}
